import { Controller } from "@hotwired/stimulus"
import { CostCalculator } from "~/helpers/costCalculator"

export default class extends Controller {
  static targets = [
    "campaignsCheckbox",
    "apiCheckbox",
    "sendingLimit",
    "emptyState",
    "result",
    "searchCountInput",
    "verificationCountInput",
    "typeRadio",
    "billingYearly",
    "billingMonthly",
    "resultContent",
    "resultHeader",
    "resultFooter",
    "billingSwitcher",
    "resultHeaderDataPlan"
  ]

  static values = {
    billingCycle: { type: String, default: "yearly" },
    quotas: Object,
    pricing: Object,
    planLevels: Object,
    currency: { type: String, default: "usd" },
    userSignedIn: Boolean
  }

  // Backend credit costs (base 10 system)
  CREDIT_COSTS = CostCalculator.OPERATION_COSTS
  UNIFIED_CREDITS_BASE = CostCalculator.UNIFIED_CREDITS_BASE

  connect() {
    // Initialize tooltips
    this.initializeTooltips()
    // Show empty state by default
    this.showEmptyState()
  }

  initializeTooltips() {
    const tooltipTriggerList = this.element.querySelectorAll('[data-bs-toggle="tooltip"]')
    tooltipTriggerList.forEach(tooltipTriggerEl => {
      new bootstrap.Tooltip(tooltipTriggerEl)
    })
  }

  toggleCampaigns() {
    const isEnabled = this.campaignsCheckboxTarget.checked

    if (isEnabled) {
      this.sendingLimitTarget.classList.remove('d-none')
      // If campaigns enabled, disable API-only (they're mutually exclusive)
      if (this.apiCheckboxTarget.checked) {
        this.apiCheckboxTarget.checked = false
      }
    } else {
      this.sendingLimitTarget.classList.add('d-none')
    }

    this.updateCalculation()
  }

  toggleApi() {
    const isEnabled = this.apiCheckboxTarget.checked

    // If API-only is selected, campaigns must be disabled (exclusive)
    if (isEnabled && this.campaignsCheckboxTarget.checked) {
      this.campaignsCheckboxTarget.checked = false
      this.sendingLimitTarget.classList.add('d-none')
    }

    this.updateCalculation()
  }

  updateInputVisibility(event) {
    const selectedValue = event?.target?.value || this.getSelectedType()

    const searchInput = this.searchCountInputTarget.closest('.mb-8')
    const verificationInput = this.verificationCountInputTarget.closest('.mb-8')

    if (selectedValue === 'finder') {
      searchInput.classList.remove('d-none')
      verificationInput.classList.add('d-none')
      this.verificationCountInputTarget.value = ''
    } else if (selectedValue === 'verifier') {
      searchInput.classList.add('d-none')
      verificationInput.classList.remove('d-none')
      this.searchCountInputTarget.value = ''
    } else if (selectedValue === 'both') {
      searchInput.classList.remove('d-none')
      verificationInput.classList.remove('d-none')
    }

    this.updateCalculation()
  }

  async updateCalculation() {
    const searches = parseInt(this.searchCountInputTarget.value) || 0
    const verifications = parseInt(this.verificationCountInputTarget.value) || 0
    const campaignsEnabled = this.campaignsCheckboxTarget.checked
    const apiOnly = this.apiCheckboxTarget.checked
    const selectedType = this.getSelectedType()

    // Show empty state if no input provided
    if (!selectedType || (searches === 0 && verifications === 0)) {
      this.showEmptyState()
      return
    }

    // Hide empty state and show result
    this.hideEmptyState()

    // Calculate monthly credits needed (backend credits)
    const monthlyCreditsNeeded = this.calculateCreditsNeeded(searches, verifications)

    // Determine plan type
    const planInfo = await this.determinePlan(monthlyCreditsNeeded, campaignsEnabled, apiOnly, searches, verifications, selectedType)

    // Display the result
    this.displayResult(planInfo)
  }

  getSelectedType() {
    const checkedRadio = this.typeRadioTargets.find(radio => radio.checked)
    return checkedRadio?.value
  }

  calculateCreditsNeeded(searches, verifications) {
    // Calculate BACKEND credits (base 10 system):
    // Search: 10 backend credits per email (shown as 1 to user)
    // Verify: 5 backend credits per verification (shown as 0.5 to user)
    const searchCredits = searches * this.CREDIT_COSTS.search
    const verifyCredits = verifications * this.CREDIT_COSTS.verify
    return searchCredits + verifyCredits
  }

  // Convert backend credits to frontend display value
  backendToFrontend(backendCredits) {
    return backendCredits / this.UNIFIED_CREDITS_BASE
  }

  // Convert frontend display value to backend credits
  frontendToBackend(frontendCredits) {
    return frontendCredits * this.UNIFIED_CREDITS_BASE
  }

  // Get currency symbol based on current currency
  getCurrencySymbol() {
    const symbols = {
      usd: '$',
      eur: '€',
      gbp: '£'
    }
    return symbols[this.currencyValue] || '$'
  }

  // Round value to nearest step (used for Data Platform which increases by steps of 1,000)
  roundToStep(value, step) {
    return Math.ceil(value / step) * step
  }

  async fetchCreditPackPrice(credits, planLevel) {
    try {
      const response = await fetch('/visitors/calculate_credit_pack_price', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || ''
        },
        body: JSON.stringify({
          credits,
          plan_level: planLevel
        })
      })

      const data = await response.json()
      return data
    } catch (error) {
      console.error('Error fetching credit pack price:', error)
      return null
    }
  }

  async calculateCreditPackDetails(excessCredits, plan) {
    // Round to nearest 1,000 backend credits
    const roundedCredits = this.roundToStep(excessCredits, 1000)
    const units = Math.ceil(roundedCredits / 1000)

    // Get plan level for subscription discounts
    const planLevel = this.planLevelsValue[plan] || 0

    const packData = await this.fetchCreditPackPrice(roundedCredits, planLevel)

    if (!packData) {
      return null
    }

    return {
      units,
      backendCredits: packData.credits,
      frontendCredits: this.backendToFrontend(packData.credits),
      price: packData.price
    }
  }

  async shouldUseCreditPacks(plan, creditsNeeded) {
    // Only for paid plans (starter, growth, scale)
    const paidPlans = ['starter', 'growth', 'scale']
    if (!paidPlans.includes(plan)) {
      return null
    }

    const planQuota = this.quotasValue[plan].unified
    const excessCredits = creditsNeeded - planQuota

    // If no excess, no packs needed
    if (excessCredits <= 0) {
      return null
    }

    // Get next tier plan
    const planIndex = paidPlans.indexOf(plan)
    const nextPlan = planIndex < paidPlans.length - 1 ? paidPlans[planIndex + 1] : null

    // Calculate pack details with plan-specific discounts
    const packDetails = await this.calculateCreditPackDetails(excessCredits, plan)
    if (!packDetails) {
      return null
    }

    const MAX_REASONABLE_PACK_PRICE = 500
    if (!nextPlan && packDetails.price > MAX_REASONABLE_PACK_PRICE) {
      // Suggest Enterprise instead for unreasonably expensive packs
      return null
    }

    if (nextPlan) {
      // Compare: current plan + packs vs next tier
      const currentPlanPrice = this.pricingValue[this.currencyValue][this.billingCycleValue][plan]
      const totalWithPacks = currentPlanPrice + packDetails.price
      const nextTierPrice = this.pricingValue[this.currencyValue][this.billingCycleValue][nextPlan]

      // Only use packs if cheaper than next tier
      if (totalWithPacks >= nextTierPrice) {
        return null
      }
    }

    return packDetails
  }

  async determinePlan(monthlyCreditsNeeded, campaignsEnabled, apiOnly, searches, verifications, selectedType) {
    // Data Platform enforced minimums and step size
    const DATA_PLATFORM_MIN = 1000
    const DATA_PLATFORM_STEP = 1000

    // If API-only is enabled and campaigns are disabled, always use Data Platform
    if (apiOnly && !campaignsEnabled) {
      let roundedSearches = 0
      let roundedVerifications = 0

      // Only apply minimums to selected credit type(s)
      if ((selectedType === 'finder' || selectedType === 'both') && searches > 0) {
        // Include searches: ensure minimum of 1,000 and round to nearest 1,000
        roundedSearches = this.roundToStep(Math.max(searches, DATA_PLATFORM_MIN), DATA_PLATFORM_STEP)
      }

      if ((selectedType === 'verifier' || selectedType === 'both') && verifications > 0) {
        // Include verifications: ensure minimum of 1,000 and round to nearest 1,000
        roundedVerifications = this.roundToStep(Math.max(verifications, DATA_PLATFORM_MIN), DATA_PLATFORM_STEP)
      }

      const price = await this.fetchDataPlatformPrice(roundedSearches, roundedVerifications)
      return {
        type: 'data-platform',
        name: 'Data Platform',
        price: price,
        searches: roundedSearches,
        verifications: roundedVerifications
      }
    }

    // For SaaS plans, find the best option (plan alone OR plan + packs)
    const plans = ['free', 'starter', 'growth', 'scale']
    let bestOption = null
    let bestPrice = Infinity

    for (let i = 0; i < plans.length; i++) {
      const plan = plans[i]
      const quota = this.quotasValue[plan]
      const planPrice = this.pricingValue[this.currencyValue][this.billingCycleValue][plan]

      // Option 1: This plan covers the needs directly
      if (monthlyCreditsNeeded <= quota.unified) {
        if (planPrice < bestPrice) {
          bestOption = {
            type: 'saas',
            plan: plan,
            name: quota.name,
            creditsIncluded: quota.unified,
            price: planPrice,
            hasCreditPacks: false
          }
          bestPrice = planPrice
        }
      }

      // Option 2: This plan + credit packs (only for paid plans)
      if (plan !== 'free' && monthlyCreditsNeeded > quota.unified) {
        const packDetails = await this.shouldUseCreditPacks(plan, monthlyCreditsNeeded)

        if (packDetails) {
          const totalPrice = planPrice + packDetails.price

          if (totalPrice < bestPrice) {
            bestOption = {
              type: 'saas',
              plan: plan,
              name: quota.name,
              creditsIncluded: quota.unified,
              price: planPrice,
              hasCreditPacks: true,
              creditPacks: packDetails
            }
            bestPrice = totalPrice
          }
        }
      }
    }

    // If we found a viable option, return it
    if (bestOption) {
      return bestOption
    }

    // If no viable option, recommend Enterprise
    return {
      type: 'enterprise',
      name: 'Enterprise',
      price: null
    }
  }

  async fetchDataPlatformPrice(searches, verifications) {
    try {
      const response = await fetch('/visitors/calculate_data_platform_price', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || ''
        },
        body: JSON.stringify({
          searches: searches,
          verifications: verifications
        })
      })

      const data = await response.json()
      return {
        total_price: data.total_price,
        search_price: data.search_price,
        verification_price: data.verification_price
      }
    } catch (error) {
      console.error('Error fetching Data Platform price:', error)
      return {
        total_price: 0,
        search_price: 0,
        verification_price: 0
      }
    }
  }

  toggleBillingCycle(event) {
    const button = event.currentTarget

    // Update active state
    this.billingYearlyTarget.classList.remove('is-active')
    this.billingMonthlyTarget.classList.remove('is-active')
    button.classList.add('is-active')

    // Update billing cycle value
    if (button === this.billingYearlyTarget) {
      this.billingCycleValue = 'yearly'
    } else {
      this.billingCycleValue = 'monthly'
    }

    // Recalculate
    this.updateCalculation()
  }

  displayResult(planInfo) {
    // Clear all result content
    this.resultContentTargets.forEach(content => {
      content.classList.add('d-none')
    })

    // Find or create the appropriate result content
    const resultContent = this.findOrCreateResultContent()
    resultContent.classList.remove('d-none')

    this.resultFooterTarget.classList.remove('d-none')
    this.resultHeaderTarget.classList.remove('d-none')

    // Update the content based on plan type
    if (planInfo.type === 'enterprise') {
      this.displayEnterpriseResult(resultContent)
    } else if (planInfo.type === 'data-platform') {
      // Use rounded values from planInfo for Data Platform
      this.displayDataPlatformResult(resultContent, planInfo, planInfo.searches, planInfo.verifications)
    } else {
      this.displaySaaSResult(resultContent, planInfo)
    }
  }

  findOrCreateResultContent() {
    // Try to find existing content div or use the first one
    return this.resultContentTargets[0]
  }

  displayEnterpriseResult(contentDiv) {
    const titleEl = contentDiv.querySelector('.plan-calculator-result__title')
    const valueEl = contentDiv.querySelector('.plan-calculator-result__value')
    const breakdownEl = contentDiv.querySelector('.plan-calculator-breakdown')
    const savingsEl = contentDiv.querySelector('.plan-calculator-result__savings')
    const footerEl = contentDiv.closest('.plan-calculator-result').querySelector('.plan-calculator-result__footer')
    const secondaryTitleEl = contentDiv.querySelector('.plan-calculator-result__value + .plan-calculator-result__title')

    // Show header with billing switcher for Enterprise
    this.resultHeaderTarget.classList.remove('d-none')
    this.billingSwitcherTarget.classList.remove('d-none')
    this.resultHeaderDataPlanTarget.classList.add('d-none')

    if (titleEl) {
      titleEl.innerHTML = i18n.t('js.plan_calculator.enterprise.recommend')
    }

    if (valueEl) {
      valueEl.innerHTML = `<small class="text-primary">${i18n.t('js.plan_calculator.enterprise.title')}</small>`
      if (!secondaryTitleEl) {
        valueEl.insertAdjacentHTML('afterend', `<p class="plan-calculator-result__title"><strong>${i18n.t('js.plan_calculator.enterprise.description')}</strong> ${i18n.t('js.plan_calculator.enterprise.additional_info')}</p>`)
      }
    }

    if (breakdownEl) {
      breakdownEl.style.display = 'none'
    }

    if (savingsEl) {
      savingsEl.style.display = 'none'
    }

    // Update CTA - disable link and add contact-sales class
    if (footerEl) {
      const ctaButton = footerEl.querySelector('.h-button--primary')
      if (ctaButton) {
        ctaButton.textContent = i18n.t('js.plan_calculator.enterprise.cta')
        ctaButton.removeAttribute('href')
        ctaButton.classList.add('contact-sales')
        // Make it a button instead of link for proper disabled state
        if (ctaButton.tagName === 'A') {
          ctaButton.style.pointerEvents = 'none'
          ctaButton.style.cursor = 'default'
        }
      }
    }
  }

  displayDataPlatformResult(contentDiv, planInfo, searches, verifications) {
    const titleEl = contentDiv.querySelector('.plan-calculator-result__title')
    const valueEl = contentDiv.querySelector('.plan-calculator-result__value')
    const breakdownEl = contentDiv.querySelector('.plan-calculator-breakdown')
    const savingsEl = contentDiv.querySelector('.plan-calculator-result__savings')
    const footerEl = contentDiv.closest('.plan-calculator-result').querySelector('.plan-calculator-result__footer')
    const secondaryTitleEl = contentDiv.querySelector('.plan-calculator-result__value + .plan-calculator-result__title')

    // Data Platform is yearly-only, so hide the entire header
    this.resultHeaderTarget.classList.add('d-none')

    if (titleEl) {
      titleEl.innerHTML = `${i18n.t('js.plan_calculator.data_platform.recommend')} <strong class="text-primary">${i18n.t('js.plan_calculator.data_platform.title')}</strong>`
    }

    if (valueEl) {
      valueEl.innerHTML = `${this.getCurrencySymbol()}${planInfo.price.total_price.toFixed(2)}`
    }

    if(secondaryTitleEl) {
      secondaryTitleEl.remove()
    }

    // Show note about yearly billing and credit steps
    if (savingsEl) {
      savingsEl.style.display = 'none'
    }

    if (breakdownEl) {
      breakdownEl.style.display = 'table'

      // Build breakdown HTML - only show rows for non-zero values
      let breakdownHTML = ''

      if (searches > 0) {
        breakdownHTML += `
          <tr>
            <th class="fw-medium">${i18n.t('js.plan_calculator.breakdown.search_credits')}</th>
            <td class="text-end">${this.formatNumber(searches)} ${i18n.t('js.plan_calculator.breakdown.credits')}</td>
            <td class="text-end">${this.getCurrencySymbol()}${planInfo.price.search_price.toFixed(2)}</td>
          </tr>
        `
      }

      if (verifications > 0) {
        breakdownHTML += `
          <tr>
            <th class="fw-medium">${i18n.t('js.plan_calculator.breakdown.verification_credits')}</th>
            <td class="text-end">${this.formatNumber(verifications)} ${i18n.t('js.plan_calculator.breakdown.credits')}</td>
            <td class="text-end">${this.getCurrencySymbol()}${planInfo.price.verification_price.toFixed(2)}</td>
          </tr>
        `
      }

      breakdownEl.innerHTML = breakdownHTML
    }

    // Update CTA
    if (footerEl) {
      const ctaButton = footerEl.querySelector('.h-button--primary')
      if (ctaButton) {
        ctaButton.textContent = i18n.t('js.plan_calculator.data_platform.cta')
        ctaButton.classList.remove('contact-sales')
        ctaButton.style.pointerEvents = ''
        ctaButton.style.cursor = ''

        if (this.userSignedInValue) {
          // Signed in users go to checkout (Data Platform is always yearly)
          ctaButton.href = `/subscriptions/checkout?billing_cycle=yearly&level=5&searches=${searches}&verifications=${verifications}`
        } else {
          // Non-signed in users go to signup with upgrade params
          ctaButton.href = `/users/sign_up?level=5&searches=${searches}&verifications=${verifications}&upgrade_after_signup=true`
        }
      }
    }
  }

  displaySaaSResult(contentDiv, planInfo) {
    const titleEl = contentDiv.querySelector('.plan-calculator-result__title')
    const valueEl = contentDiv.querySelector('.plan-calculator-result__value')
    const breakdownEl = contentDiv.querySelector('.plan-calculator-breakdown')
    const savingsEl = contentDiv.querySelector('.plan-calculator-result__savings')
    const footerEl = contentDiv.closest('.plan-calculator-result').querySelector('.plan-calculator-result__footer')
    const secondaryTitleEl = contentDiv.querySelector('.plan-calculator-result__value + .plan-calculator-result__title')

    // Show header with billing switcher for SaaS plans
    this.resultHeaderTarget.classList.remove('d-none')
    this.billingSwitcherTarget.classList.remove('d-none')
    this.resultHeaderDataPlanTarget.classList.add('d-none')

    // Calculate total price (plan + packs if applicable)
    const totalPrice = planInfo.hasCreditPacks
      ? planInfo.price + planInfo.creditPacks.price
      : planInfo.price

    // Update title
    if (titleEl) {
      const planName = planInfo.hasCreditPacks
        ? i18n.t('js.plan_calculator.saas.plan_with_packs', { name: planInfo.name })
        : i18n.t('js.plan_calculator.saas.plan_only', { name: planInfo.name })
      titleEl.innerHTML = `${i18n.t('js.plan_calculator.saas.recommend')} <strong class="text-primary">${planName}</strong>`
    }

    if(secondaryTitleEl) {
      secondaryTitleEl.remove()
    }

    // Update price display
    const priceDisplay = totalPrice === 0 ? i18n.t('js.plan_calculator.pricing.free') : `${this.getCurrencySymbol()}${totalPrice.toFixed(2)}`
    const perMonth = i18n.t('js.plan_calculator.pricing.per_month')

    if (valueEl) {
      if (totalPrice === 0) {
        valueEl.innerHTML = priceDisplay
      } else {
        valueEl.innerHTML = `${priceDisplay}<em>${perMonth}</em>`
      }
    }

    // Show savings for yearly billing (non-free plans)
    if (savingsEl && this.billingCycleValue === 'yearly' && totalPrice > 0) {
      const monthlyPlanPrice = this.pricingValue[this.currencyValue].monthly[planInfo.plan]
      const monthlyPackPrice = planInfo.hasCreditPacks ? planInfo.creditPacks.price : 0
      const monthlyTotalPrice = monthlyPlanPrice + monthlyPackPrice

      const yearlyTotalPerMonth = totalPrice
      const yearlyTotal = yearlyTotalPerMonth * 12
      const monthlyTotal = monthlyTotalPrice * 12

      savingsEl.style.display = 'block'
      savingsEl.innerHTML = `
        <span class="text-primary">${this.getCurrencySymbol()}${monthlyTotal.toFixed(0)}</span>
        ${this.getCurrencySymbol()}${yearlyTotal.toFixed(0)} ${i18n.t('js.plan_calculator.pricing.billed_annually')}
      `
    } else if (savingsEl) {
      savingsEl.style.display = 'none'
    }

    // Build breakdown
    if (breakdownEl) {
      breakdownEl.style.display = 'table'
      const frontendCredits = this.backendToFrontend(planInfo.creditsIncluded)

      // For yearly billing, show plan credits per year (12 months)
      const planCreditsMultiplier = this.billingCycleValue === 'yearly' ? 12 : 1
      const planCreditsLabel = this.billingCycleValue === 'yearly' ? i18n.t('js.plan_calculator.pricing.per_year') : i18n.t('js.plan_calculator.pricing.per_month')
      const displayPlanCredits = frontendCredits * planCreditsMultiplier

      let breakdownHTML = `
        <tr>
          <th class="fw-medium">${i18n.t('js.plan_calculator.breakdown.plan', { name: planInfo.name })}</th>
          <td class="text-end">${this.formatNumber(displayPlanCredits)} ${i18n.t('js.plan_calculator.breakdown.credits')}${planCreditsLabel}</td>
          <td class="text-end">${this.getCurrencySymbol()}${planInfo.price.toFixed(2)} ${this.currencyValue.toUpperCase()}</td>
        </tr>
      `

      // Add credit packs row if applicable
      // Credit packs are NOT multiplied - they're a one-time purchase
      if (planInfo.hasCreditPacks) {
        breakdownHTML += `
          <tr>
            <th class="fw-medium">${i18n.t('js.plan_calculator.breakdown.credit_packs')}</th>
            <td class="text-end">${this.formatNumber(planInfo.creditPacks.frontendCredits)} ${i18n.t('js.plan_calculator.breakdown.credits')}</td>
            <td class="text-end">${this.getCurrencySymbol()}${planInfo.creditPacks.price.toFixed(2)} ${this.currencyValue.toUpperCase()}</td>
          </tr>
        `
      }

      breakdownEl.innerHTML = breakdownHTML
    }

    // Update CTA
    if (footerEl) {
      const ctaButton = footerEl.querySelector('.h-button--primary')
      if (ctaButton) {
        const ctaText = planInfo.hasCreditPacks
          ? i18n.t('js.plan_calculator.saas.cta_with_packs', { name: planInfo.name })
          : i18n.t('js.plan_calculator.saas.cta_only', { name: planInfo.name })
        ctaButton.textContent = ctaText
        ctaButton.classList.remove('contact-sales')
        ctaButton.style.pointerEvents = ''
        ctaButton.style.cursor = ''

        // Build signup URL with level and upgrade parameters
        const planLevel = this.planLevelsValue[planInfo.plan]

        if (this.userSignedInValue) {
          // Signed in users go to checkout with billing cycle
          ctaButton.href = `/subscriptions/checkout?billing_cycle=${this.billingCycleValue}&level=${planLevel}`
        } else {
          // Non-signed in users go to signup with upgrade params
          ctaButton.href = `/users/sign_up?level=${planLevel}&upgrade_after_signup=true`
        }
      }
    }
  }

  formatNumber(num) {
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
  }

  showEmptyState() {
    if (this.hasEmptyStateTarget && this.hasResultTarget) {
      this.emptyStateTarget.classList.remove('d-none')
      this.resultTarget.classList.add('d-none')
      this.resultHeaderTarget.classList.add('d-none')
      this.resultFooterTarget.classList.add('d-none')
    }
  }

  hideEmptyState() {
    if (this.hasEmptyStateTarget && this.hasResultTarget) {
      this.emptyStateTarget.classList.add('d-none')
      this.resultTarget.classList.remove('d-none')
    }
  }
}

